import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import EnhancedSection from './components/EnhancedSection';
import { Field, FieldRow, FormFooter } from '../components';
import RegionDropdown from '../RegionDropdown';
import LocaleDropdown from '../LocaleDropdown';
import { useTranslation } from 'react-i18next';
import { useQueryAvailableRegions } from '../useAvailableRegions';
import { useQueryAvailableLocales } from '../useAvailableLocales';
import {
  Alert,
  Icons,
  Table,
  TextButton,
  Toggle,
} from '@shopline/dashboard-ui';
import Divider from '../../../components/Divider';
import EmptyLocale from '../components/EmptyLocale';
import { Actions, useConfigurationForm } from 'contexts/ConfigurationForm';
import { ITableHeadColumn } from '@shopline/dashboard-ui/types/components/table';
import { RegionAndLocaleSet } from 'contexts/ConfigurationForm/types';
import usePermissions from 'hooks/usePermissions';
import { SectionId } from './constants';

const EnhancedFieldRow = styled(FieldRow)`
  margin-top: 16px;
  margin-bottom: 0px;

  > div {
    margin-top: 0;
  }
`;

const KEY_PREFIX = 'DATA_TABLE';
const KEYS = {
  REGION: `${KEY_PREFIX}.REGION`,
  LOCALE: `${KEY_PREFIX}.LOCALE`,
  ACTION: `${KEY_PREFIX}.ACTION`,
};
const COLUMNS = [
  { key: KEYS.REGION },
  { key: KEYS.LOCALE },
  { key: KEYS.ACTION },
];

const RegionAndLocaleSetsInputs = (props?: { onChange?: () => void }) => {
  const { t } = useTranslation(['main', 'common']);
  const { data: regions } = useQueryAvailableRegions();
  const { data: locales } = useQueryAvailableLocales();
  const {
    state: {
      editingState: { regionAndLocaleSets },
      formErrors,
    },
    dispatch,
  } = useConfigurationForm();
  const [regionValue, setRegionValue] = useState('');
  const [localeValue, setLocaleValue] = useState('');
  const canEdit = usePermissions('merchant', 'edit');

  // select default value
  useEffect(() => {
    setRegionValue(regions[0]?.value || '');
    setLocaleValue(locales[0]?.value || '');
  }, [setRegionValue, setLocaleValue, regions, locales]);

  const addRegionAndLocaleSet = () => {
    dispatch(
      Actions.addRegionAndLocaleSet({
        region: regionValue,
        locale: localeValue,
        enabled: true,
      }),
    );
    props?.onChange?.();
  };
  const toggleRegionAndLocaleSetEnabled = (
    regionAndLocaleSet: RegionAndLocaleSet,
  ) => {
    dispatch(Actions.toggleRegionAndLocaleSetEnabled(regionAndLocaleSet));
    props?.onChange?.();
  };

  const columns = COLUMNS.map<ITableHeadColumn>((column) => ({
    ...column,
    align: 'center',
    title: t(column.key),
  }));
  const dataSources = regionAndLocaleSets.map((regionAndLocaleSet) => ({
    [KEYS.REGION]: t(`REGION.${regionAndLocaleSet.region}`),
    [KEYS.LOCALE]: t(`LOCALE.${regionAndLocaleSet.locale}`),
    [KEYS.ACTION]: () => (
      <Toggle
        isToggledOn={regionAndLocaleSet.enabled}
        onClick={() => toggleRegionAndLocaleSetEnabled(regionAndLocaleSet)}
        disabled={!canEdit}
      />
    ),
  }));

  return (
    <EnhancedSection
      title={t('Ad Placement Area and Language')}
      id={SectionId.REGION_AND_LOCALE_SETS}
    >
      <EnhancedSection.Description>
        {t(
          'The system will upload your products (including preorder and hidden products) to Google Merchant Center. Product ads will be displayed based on the selected placement area and language.',
        )}
      </EnhancedSection.Description>
      <Alert.Information>
        {t(
          'Please enter the product name, summary, and description for the selected placement language to ensure successful product uploads.',
        )}
      </Alert.Information>
      <EnhancedFieldRow>
        <Field title={t('Region')}>
          <RegionDropdown
            value={[regionValue]}
            onChange={(value) => setRegionValue(value)}
            disabled={!canEdit}
          />
        </Field>
        <Field title={t('Locale')}>
          <LocaleDropdown
            value={[localeValue]}
            onChange={(value) => setLocaleValue(value)}
            disabled={!canEdit}
          />
        </Field>
      </EnhancedFieldRow>
      <FormFooter>
        <TextButton.Primary
          onClick={addRegionAndLocaleSet}
          disabled={!regionValue || !localeValue || !canEdit}
        >
          <Icons.Outline.Plus size="SMALL" />
          {t('Add a language to the list below')}
        </TextButton.Primary>
      </FormFooter>
      <Divider spacing="2rem" />
      {regionAndLocaleSets.length === 0 ? (
        <>
          {formErrors.regionAndLocaleSets && (
            <Alert.Destructive>
              {t('Placement area and placement language are not configured')}
            </Alert.Destructive>
          )}
          <EmptyLocale />
        </>
      ) : (
        <Table
          headColumnAlign="center"
          rowVerticalAlign="middle"
          columns={columns}
          dataSource={dataSources}
          lang="en"
        />
      )}
    </EnhancedSection>
  );
};

export default RegionAndLocaleSetsInputs;
